import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useJWTAuth } from "../../context/JWTAuthContext";
import {
  handleAuthCallback,
  logUserConsent,
} from "../../services/auth/authService";
import logger from "../../utils/logger";
import { AUTH_TRANSITION_STORAGE_KEY } from "../../utils/authStartup";
import { handleSuccessfulAuthCallback } from "./authCallbackFlow";

/**
 * Компонент для обработки callback после клика на Magic Link
 * URL: /auth/callback?token=xxx&type=magiclink
 */
const AuthCallback = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useJWTAuth();
  const [status, setStatus] = useState("loading"); // loading | success | error
  const [error, setError] = useState(null);

  useEffect(() => {
    const processCallback = async () => {
      try {
        logger.info("AuthCallback: processing redirect");
        setStatus("loading");

        // Обрабатываем auth callback
        const { user, session, role } = await handleAuthCallback();

        if (!session) {
          throw new Error("Не удалось получить сессию");
        }

        sessionStorage.setItem(AUTH_TRANSITION_STORAGE_KEY, String(Date.now()));

        await handleSuccessfulAuthCallback({
          user,
          role,
          setStatus,
          logUserConsent,
          logger,
        });
      } catch (err) {
        logger.error("AuthCallback error", err);
        setError(err.message || "Ошибка авторизации");
        setStatus("error");

        // Редирект на login через 3 секунды
        setTimeout(() => {
          navigate("/login", { replace: true });
        }, 3000);
      }
    };

    processCallback();
  }, [navigate]);

  useEffect(() => {
    if (status === "success" && isAuthenticated) {
      sessionStorage.removeItem(AUTH_TRANSITION_STORAGE_KEY);
      navigate("/dashboard", { replace: true });
    }
  }, [status, isAuthenticated, navigate]);

  useEffect(() => {
    if (status !== "success") {
      return undefined;
    }

    const fallbackId = setTimeout(() => {
      navigate("/dashboard", { replace: true });
    }, 1500);

    return () => clearTimeout(fallbackId);
  }, [status, navigate]);

  // Рендер в зависимости от статуса
  if (status === "loading") {
    return (
      <div style={styles.container}>
        <div style={styles.spinner}></div>
        <h2 style={styles.title}>Авторизация...</h2>
        <p style={styles.text}>Пожалуйста, подождите</p>
      </div>
    );
  }

  if (status === "success") {
    return (
      <div style={styles.container}>
        <div style={styles.successIcon}>✅</div>
        <h2 style={styles.title}>Успешно!</h2>
        <p style={styles.text}>Перенаправление...</p>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div style={styles.container}>
        <div style={styles.errorIcon}>❌</div>
        <h2 style={styles.title}>Ошибка авторизации</h2>
        <p style={styles.errorText}>{error}</p>
        <p style={styles.text}>Перенаправление на страницу входа...</p>
      </div>
    );
  }

  return null;
};

// Простые inline стили (можно заменить на CSS модули)
const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
    padding: "24px",
    backgroundColor: "#f8fafc",
  },
  spinner: {
    width: "48px",
    height: "48px",
    border: "4px solid #e2e8f0",
    borderTop: "4px solid #3b82f6",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
  successIcon: {
    fontSize: "64px",
    marginBottom: "16px",
  },
  errorIcon: {
    fontSize: "64px",
    marginBottom: "16px",
  },
  title: {
    fontSize: "24px",
    fontWeight: "600",
    color: "#1a202c",
    marginTop: "16px",
    marginBottom: "8px",
  },
  text: {
    fontSize: "16px",
    color: "#4a5568",
    marginTop: "8px",
  },
  errorText: {
    fontSize: "14px",
    color: "#ef4444",
    marginTop: "8px",
    textAlign: "center",
    maxWidth: "400px",
  },
};

export default AuthCallback;
